import type { ObjectColor } from '@ofmos/shared/object-color';
import {
  Icon,
  isDrawingName,
  isGlyphName,
  MonoSymbol,
  type ObjectIcon,
  ObjectTile,
  useIsDesktop,
} from '../../ui';
import { type SidebarIcons, useSidebarIcons } from '../session';

/**
 * WHAT A NAVIGATION ROW LEADS WITH, AND IT IS ONE COMPONENT FOR EVERY LIST (wave 5, package A3,
 * decisions 2 and 3 of 2026-09-17; redesign register A5-2, A5-3 and A5-6 of 2026-10-04).
 *
 * EVERY ROW COMES THROUGH HERE: the app sidebar's fixed rows, the Fulfillment section, the
 * object list, the Settings list, the person's own rows at the foot of the sidebar, and the
 * phone's Data screen and More sheet. The owner's rule (A5-3) is one symbol set, one point size
 * and one colour per context, and Apple's is the same: "A sidebar's row height, text, and glyph
 * size depend on its overall size" (hig:
 * https://developer.apple.com/design/human-interface-guidelines/sidebars), so the size belongs
 * to the context and never to the row. Lists used to choose for themselves - three of them
 * branched on the pointer, the Settings list did not, so the phone's More sheet drew the
 * desktop tile under the 28px tile of Pending changes - and two sizes stood in one list.
 *
 * TWO CONTEXTS, ONE SIZE EACH:
 * - The desktop sidebar: `--nav-tile-edge`, the kit's 16 / 20 / 24 under the person's "Sidebar
 *   icon size". SYMBOLS draws the mono form (`ui/ObjectGlyph/MonoSymbol.tsx`) in the accent (hig:
 *   Sidebars, "By default, sidebar icons use your app's accent color"): one silhouette per glyph
 *   or one of our drawings, every one on the same optical box, painted in `currentColor` only.
 *   TILES draws the coloured System Settings tile with the four icon styles still acting on it.
 * - Below the desktop breakpoint: the fixed 28px tile of an iOS list row, whatever the desktop
 *   setting says, because the setting is a desktop setting exactly as it is on a Mac
 *   (`tokens.css`, `[data-sidebar-size]`). The phone's own symbol round is register P6.
 *
 * WHAT IS DRAWN under Symbols: the mono form of one of the thirty-one glyphs or of one of our
 * drawings, at the box's size. A hairline `Icon` is left only for an icon an operator picked that
 * is neither; it is not part of the set and draws at the hairline size it always had.
 */
export function NavIcon({
  icon,
  color = null,
  construction,
}: {
  readonly icon: ObjectIcon;
  readonly color?: ObjectColor | null;
  /**
   * THE STATES PAGE IS THE ONE CALLER THAT NAMES IT. A gallery has to show both ways at once
   * - that is what a gallery is for - and it cannot do that by flipping the person's own
   * setting. Every real row leaves this undefined and follows the preference.
   */
  readonly construction?: SidebarIcons | undefined;
}) {
  const isDesktop = useIsDesktop();
  const preference = useSidebarIcons();
  if (!isDesktop && construction === undefined) {
    return <ObjectTile icon={icon} color={color} size="md" />;
  }
  const icons = construction ?? preference;
  if (icons === 'tiles') return <ObjectTile icon={icon} color={color} size="nav" />;
  return (
    <span aria-hidden="true" className="shell-nav-symbol">
      {isGlyphName(icon) || isDrawingName(icon) ? (
        <MonoSymbol name={icon} size="100%" />
      ) : (
        <Icon name={icon} size="var(--nav-tile-glyph)" />
      )}
    </span>
  );
}
